/**
 * [ScenarioBasicInfo] Step 1 of the Scenario module, provides the form section for editing/viewing
 * the basic details of a Scenario record (e.g. name, description, and associated baseline).
 * - Uses local state to manage form fields, syncing with parent (ScenarioMain.jsx) via props and callbacks.
 * - Applies standardized field validation via verifyFields().
 *
 * @component
 * @param {Object} props
 * @param {Object} props.item The current scenario record to edit (from Redux or parent).
 * @param {string} [props.mode="edit"] Controls editability of the form.
 * @param {string} [props.size] UI size for text fields/selects.
 * @param {number} [props.formFieldWidth] Width for selects.
 * @param {function(Object):void} props.onLocalScenarioChange Callback to bubble up changed scenario data.
 * @param {function(boolean):void} props.onValidateData Callback to notify parent about validation status.
 * @param {function(boolean):void} props.onSetDataChanged Callback to notify parent when user changes data.
 * @param {function():void} [props.onClearComplete] Callback to notify when the form has been cleared/reset.
 * @returns {React.ReactElement}
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";

// Custom hooks
import { useLookupsData } from "src/context/LookupsContext";
import { useBaselinesData } from "src/hooks/useBaselinesData";

// Layout/UI
import {
	Box,
	createListCollection,
	Field,
	HStack,
	MetreaSelectContent,
	MetreaSelectItem,
	MetreaSelectLabel,
	MetreaSelectRoot,
	MetreaSelectTrigger,
	MetreaSelectValueText,
	Portal,
	Separator,
	Stack,
	Text,
} from "@metrea-ui/v2";
import FormTextField from "src/components/UI/common/FormTextField";
import BaselineBasicInfo from "../Baseline/BaselineBasicInfo";
import BaselineConfigs from "../Baseline/BaselineConfigs";

// Utilities
import { baselineAccess, extendBaseline } from "src/utils/baselineUtils";
import { deepClone } from "src/utils/dataUtils";
import { Session } from "src/utils/sessionUtils";
import { debugLog } from "src/utils/debugUtils";
import { verifyFields } from "src/utils/formValidationUtils";
import { mColor } from "src/utils/themeUtils";

const getSelectLabel = (item) =>
	item?.label ??
	item?.extended_name ??
	item?.display_name ??
	item?.name ??
	item?.comp_value ??
	(item?.code && item?.symbol ? `${item.code} - ${item.symbol}` : undefined) ??
	item?.code ??
	item?.title ??
	item?.text ??
	String(item?.id ?? "");

const sizeToSelectSize = (size) => {
	if (size === "large") return "md";
	if (size === "small") return "sm";
	return size || "sm";
};

const buildSelectCollectionItems = (items = []) =>
	items.map((item) => ({
		label: getSelectLabel(item),
		value: String(item?.id ?? item?.value ?? ""),
		rawValue: item?.id ?? item?.value ?? "",
	}));

const sizeToFieldControl = (size) => {
	if (size === "large") return "md";
	if (size === "small") return "sm";
	return size || "sm";
};

const ScenarioBasicInfo = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	// Redux Selectors
	const dispatch = useDispatch();
	const baselines = useSelector((state) => state.baselines.list);

	const uiSize = props.size;
	const isDisabled = props.mode !== "edit";
	const onValidateDataRef = useRef(props.onValidateData);

	// --- Local State ---
	// Local copy of the current scenario (driven from props)
	const initialScenarioSafe = props.item && typeof props.item === "object" ? props.item : {};
	const [currScenario, setCurrScenario] = useState(initialScenarioSafe);
	const [nameError, setNameError] = useState("");
	const [baselineError, setBaselineError] = useState("");
	const [baselinesFiltered, setBaselinesFiltered] = useState([]);

	// Custom hooks
	const { allUserGroups } = useLookupsData();

	const { fetchBaselines } = useBaselinesData();

	// Keep the shared field validators stable so the parent validation effect
	// runs for scenario data changes instead of validator identity churn.
	const validators = useMemo(
		() => [
			{
				field: "name",
				validate: (v) => v !== "",
				setError: setNameError,
				message: "Scenario name is required!",
			},
			{
				field: "baseline_id",
				validate: (v) => v !== null && v !== undefined && String(v).trim() !== "",
				setError: setBaselineError,
				message: "Baseline is required!",
			},
		],
		[]
	);
	const verifyDataFields = useCallback((data) => verifyFields(data, validators), [validators]);

	// --- Effect: Load user groups and baselines on mount
	useEffect(() => {
		debugLog(2, "[ScenarioBasicInfo][FLOW] mounted", {});
		if (!baselines || baselines.length === 0) {
			debugLog(2, "[ScenarioBasicInfo][FLOW] fetchBaselines started", {});
			fetchBaselines();
		}
		debugLog(2, "[ScenarioBasicInfo][FLOW] initial fetch check", { baselines: baselines });
	}, [dispatch, baselines, fetchBaselines]);

	// Parent handlers are recreated during parent renders; keep the latest callback
	// without treating callback identity churn as a reason to revalidate unchanged data.
	useEffect(() => {
		onValidateDataRef.current = props.onValidateData;
	}, [props.onValidateData]);

	// --- Effect: Filter baselines based on access, to prepare for baseline dropdown
	useEffect(() => {
		if (baselines && baselines.length > 0) {
			const filtered = baselines.filter(
				(baseline) =>
					baselineAccess(baseline, allUserGroups, Session().currUser) >= 1 &&
					baseline.status_id === 1, // only active baselines
			);

			// add additional baseline info to the name
			const filteredClone = filtered.map(extendBaseline);

			setBaselinesFiltered(filteredClone);
			debugLog(4, "[ScenarioBasicInfo][PROCESS] filter baselines", {
				baselines: baselines,
				filtered: filteredClone,
			});
		}
	}, [baselines, allUserGroups]);

	// --- Effect: sync local when parent item changes
	useUpdateEffect(() => {
		if (props.item && typeof props.item === "object") {
			setCurrScenario(deepClone(props.item));
		} else {
			setCurrScenario({});
		}
		debugLog(3, "[ScenarioBasicInfo][STATE] props.item changed", { propsItem: props.item });
	}, [props.item]);

	// Bubble up the latest validation result for parent save/navigation guards.
	// verifyFields owns these required-field errors, so data changes are the trigger.
	useEffect(() => {
		const hasError = verifyDataFields(currScenario);
		onValidateDataRef.current(hasError);
		debugLog(3, "[ScenarioBasicInfo][STATE] field validation updated", {
			currScenario: currScenario,
			hasError: hasError,
		});
	}, [currScenario, verifyDataFields]);

	// --- Utility Functions ---
	// Return True if editing a new scenario.
	const isNew = () => !currScenario?.id; // Prevent running logic if currScenario is null/undefined

	// --- Handlers ---
	const handleInputChange = (event) => {
		const { name, value } = event.target;
		const newName = name.replace(/scenario_/g, "");
		debugLog(7, "[ScenarioBasicInfo][ACTION] handleInputChange", { name, value, newName });

		// Update local state and alert parent of changes
		const updateData = {
			...currScenario,
			[newName]: value,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};
		setCurrScenario(updateData);
		props.onSetDataChanged(true);

		// Notify parent component of changes.
		props.onLocalScenarioChange(updateData);
	};

	const chooseBaselineHandler = (name, selected) => {
		if (selected) setBaselineError("");

		const updateData = {
			...currScenario,
			baseline_id: selected,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};
		setCurrScenario(updateData);
		props.onSetDataChanged(true);
		props.onLocalScenarioChange(updateData);

		debugLog(3, "[ScenarioBasicInfo][ACTION] chooseBaselineHandler", {
			name: name,
			selected: selected,
			updateData: updateData,
		});
	};

	// --- Logging: Render and State Snapshots ---
	debugLog(9, "[ScenarioBasicInfo][FLOW] render", {
		renderCount: renderCount.current,
		props: props,
	});
	debugLog(9, "[ScenarioBasicInfo][FLOW] renderState", {
		baselinesFiltered: baselinesFiltered,
		currScenario: currScenario,
	});

	// Field helpers keep the error text reusable after moving off the shared single-select wrapper.
	const baselineProps = baselineError ? { error: true, helperText: baselineError } : {};
	const scenarioNameProps = nameError ? { error: true, helperText: nameError } : {};

	// Plain text/textarea fields now share the stable direct-v2 helper, while
	// the Baseline picker and other scenario-specific controls stay local.
	const renderTextField = (props) => (
		<FormTextField {...props} size={sizeToFieldControl(uiSize)} helperColor={mColor.Danger} />
	);
	const baselineCollection = useMemo(
		() =>
			createListCollection({
				// Keep the Scenario baseline picker to real baseline choices only.
				// This screen does not need an explicit clear-to-empty option in the dropdown.
				items: buildSelectCollectionItems(baselinesFiltered),
			}),
		[baselinesFiltered]
	);

	const renderBaselineSelect = () => {
		const helpId = "baseline_id-helptext";
		const hasHelperContent = Boolean(baselineProps.helperText);
		const currentValue = currScenario?.baseline_id == null ? "" : String(currScenario.baseline_id);

		return (
			<Box width={props.formFieldWidth || "100%"} mt="1rem" mb="0.5rem">
				<Field.Root required invalid={Boolean(baselineProps.error)} style={{ width: "100%" }}>
					<MetreaSelectRoot
						variant="outline"
						collection={baselineCollection}
						size={sizeToSelectSize(uiSize)}
						width="100%"
						name="baseline_id"
						value={currentValue ? [currentValue] : []}
						invalid={Boolean(baselineProps.error)}
						disabled={isDisabled}
						onValueChange={(details) => {
							// Keep the baseline picker on the same numeric-id contract the rest of the
							// scenario flow already expects while moving the visible control to direct v2 Select.
							const raw = Array.isArray(details?.value) ? String(details.value[0] ?? "") : "";
							chooseBaselineHandler("baseline_id", raw === "" ? "" : Number(raw));
						}}
					>
						<MetreaSelectLabel>
							Baseline
							<Field.RequiredIndicator />
						</MetreaSelectLabel>
						<MetreaSelectTrigger
							id="baseline_id"
							disabled={isDisabled}
							aria-invalid={baselineProps.error ? "true" : undefined}
							aria-describedby={helpId}
						>
							<MetreaSelectValueText placeholder="Select a baseline..." />
						</MetreaSelectTrigger>
						<Portal>
							<MetreaSelectContent>
								{baselineCollection.items.map((item) => (
									<MetreaSelectItem key={item.value} item={item}>
										{item.label}
									</MetreaSelectItem>
								))}
							</MetreaSelectContent>
						</Portal>
					</MetreaSelectRoot>
					<Field.HelperText id={helpId}>
						<span
							style={{
								display: "block",
								minHeight: "1.5em",
								fontSize: "0.75rem",
								color: baselineProps.error ? "var(--metrea-colors-border-danger, #F04438)" : "inherit",
								visibility: hasHelperContent ? "visible" : "hidden",
							}}
						>
							{hasHelperContent ? baselineProps.helperText : "\u00A0"}
						</span>
					</Field.HelperText>
				</Field.Root>
			</Box>
		);
	};

	return (
		<Stack>
			{isNew() && (
				<>
					{/* Baseline dropdown */}
					{renderBaselineSelect()}
				</>
			)}
			{currScenario && (
				<>
					{/* Name */}
					{renderTextField({
						id: "scenario_name",
						name: "scenario_name",
						label: "Scenario Name",
						width: "60%",
						required: true,
						value: currScenario?.name ?? "",
						maxLength: 64,
						onChange: handleInputChange,
						disabled: isDisabled,
						errorText: scenarioNameProps.helperText,
					})}

					{/* Description */}
					{renderTextField({
						id: "scenario_description",
						name: "scenario_description",
						label: "Description / Note",
						width: "80%",
						multiline: true,
						rows: 6,
						value: currScenario?.description ?? "",
						onChange: handleInputChange,
						disabled: isDisabled,
					})}

					<Box mt={20} />

					{!isNew() && (
						<>
							{/* Baseline Details */}
							<HStack>
								<Text textStyle="xs" flexShrink="0">
									Baseline Details
								</Text>
								<Separator flex="1" />
							</HStack>

							<Box w="100%">
								<BaselineBasicInfo
									formFieldWidth={props.formFieldWidth}
									item={currScenario.baseline}
									mode="read"
									onUpdateBaselineStep={() => void 0}
									onSetCurrentBaseline={() => void 0}
									onValidateData={() => void 0}
									onClearComplete={() => void 0}
								/>
							</Box>

							<Box mt={20} />

							<Box w="100%">
								<BaselineConfigs
									formFieldWidth={props.formFieldWidth}
									item={currScenario.baseline}
									mode="read"
									groupId={100}
									onSaveDataToDB={() => void 0}
									onSetCurrentBaseline={() => void 0}
									onClearComplete={() => void 0}
								/>
							</Box>
						</>
					)}
				</>
			)}
		</Stack>
	);
};

export default ScenarioBasicInfo;
